import { cellTypes } from "@visiblethread/react-components/dist/components/Table/TableCell";
import CustomCell from "@visiblethread/react-components/dist/components/Table/cells/CustomCell";
import TextCell from "@visiblethread/react-components/dist/components/Table/cells/TextCell";
import CheckboxCell from "@visiblethread/react-components/dist/components/Table/cells/CheckboxCell";
import ButtonCell from "@visiblethread/react-components/dist/components/Table/cells/ButtonCell";
import AddChildrenCell from "@visiblethread/react-components/dist/components/Table/cells/AddChildrenCell";
import React from "react";
import Wildcard from "layout/dictionary/edit/cells/Wildcard";
import Colour from "layout/dictionary/edit/cells/Colour";
import { ReactComponent as Trash } from "icons/trash.svg";
import {
  entryTypes,
  sortingModes,
  dictionaryTermTypes,
} from "constants/dictionary";
import {
  editCategory,
  editTerm,
  addTerm,
  deleteTerm,
  deleteCategory,
  validateCategories,
  validateTerms,
} from "slices/dictionary/dictionarySlice";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import CategoryName from "layout/dictionary/edit/cells/CategoryName";
import TermName from "layout/dictionary/edit/cells/TermName";
import { dictionaryDeleteTermModal } from "components/Modal/confModalTypes";
import { createEmptyTerm } from "utils/dictionary";

export const tableMapper = (dispatch, isEditMode) => {
  const deleteEntry = (entry) => {
    if (entry.type === entryTypes.TERM) {
      dispatch(
        deleteTerm({
          categoryId: entry.categoryId,
          termId: entry.id,
        })
      );

      dispatch(validateTerms());
    }
    if (entry.type === entryTypes.CATEGORY) {
      dispatch(
        deleteCategory({
          categoryId: entry.id,
        })
      );

      dispatch(validateCategories());
      // Also validating terms, because after category is deleted all terms inside are also deleted
      dispatch(validateTerms());
    }
  };

  const handleDeleteButtonClick = (entry) => {
    if (entry.name === "") {
      deleteEntry(entry);
    } else {
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          dictionaryDeleteTermModal(
            entry,
            () => {
              deleteEntry(entry);
            },
            dispatch
          )
        )
      );
    }
  };

  const sortFunction = (rows, sortingMode) => {
    const isParent = (row) => {
      return row.parentIndex !== undefined;
    };
    const isAddNewRow = (row) => {
      return row?.content[0]?.props?.addChildrenText;
    };
    let sortedRows = [];
    let children = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (isParent(row) || isAddNewRow(row)) {
        sortedRows.push(row);
      } else {
        children.push(row);
        if (!rows[i + 1] || isAddNewRow(rows[i + 1]) || isParent(rows[i + 1])) {
          children.sort((a, b) => {
            const aText = a.content[0].props.text;
            const bText = b.content[0].props.text;
            if (sortingMode === sortingModes.ascending) {
              if (aText < bText) {
                return 1;
              }
              if (aText > bText) {
                return -1;
              }
            } else {
              if (aText < bText) {
                return -1;
              }
              if (aText > bText) {
                return 1;
              }
            }
            return 0;
          });
          sortedRows = sortedRows.concat(children);
          children = [];
        }
      }
    }
    sortedRows.forEach((row, index) => {
      row.id = index;
    });
    return sortedRows;
  };

  const getHeader = () => [
    {
      text: "Terms",
      minWidth: 238,
      defaultWidth: 238,
      cellType: cellTypes.text,
      sortFunction,
    },
    {
      text: "Case Sensitive",
      minWidth: 80,
      defaultWidth: 80,
      cellType: cellTypes.checkbox,
    },
    {
      text: "Highlight Color",
      minWidth: 80,
      defaultWidth: 80,
      cellType: cellTypes.custom,
      centeredHeader: true,
    },
    {
      text: "Description",
      minWidth: 300,
      flexGrow: 1,
      cellType: cellTypes.text,
      autoResizable: true,
    },
    {
      text: "Wildcard",
      minWidth: 80,
      defaultWidth: 80,
      cellType: cellTypes.custom,
      centeredHeader: true,
    },
    { text: "", minWidth: 50, defaultWidth: 50, cellType: cellTypes.button },
  ];

  const getCategoryContent = (c) => {
    const handleEditorClose = () => {
      dispatch(
        editCategory({
          categoryId: c.id,
          editedCategory: {
            ...c,
            newlyCreated: false,
          },
        })
      );
    };
    return [
      <CustomCell
        content={
          <CategoryName
            category={c}
            isEditMode={isEditMode}
            onEditorClose={handleEditorClose}
          />
        }
      />,
      null,
      <CustomCell
        content={
          <Colour categoryId={c.id} colour={c.colour} disabled={!isEditMode} />
        }
      />,
      <TextCell text={c.description} />,
      null,
      <ButtonCell
        content={<Trash />}
        onClick={() => handleDeleteButtonClick(c)}
      />,
    ];
  };

  const getTermContent = (term) => {
    const handleEditorClose = () => {
      dispatch(
        editTerm({
          termId: term.id,
          categoryId: term.categoryId,
          editedTerm: { ...term, newlyCreated: false },
        })
      );
    };

    return [
      <CustomCell
        content={
          <TermName
            term={term}
            isEditMode={isEditMode}
            disabled={
              isEditMode &&
              term.dictionaryTermType !== dictionaryTermTypes.STRING
            }
            onEditorClose={handleEditorClose}
          />
        }
      />,
      <CheckboxCell
        checked={term.caseSensitive}
        disabled={
          term.dictionaryTermType !== dictionaryTermTypes.STRING || !isEditMode
        }
      />,
      <CustomCell
        content={
          <Colour
            categoryId={term.categoryId}
            termId={term.id}
            colour={term.colour}
            disabled={
              !isEditMode ||
              term.dictionaryTermType !== dictionaryTermTypes.STRING
            }
          />
        }
      />,
      <TextCell
        text={term.description}
        disabled={
          term.dictionaryTermType !== dictionaryTermTypes.STRING && isEditMode
        }
        viewOnly={!isEditMode}
      />,
      <CustomCell content={<Wildcard term={term} />} />,
      <ButtonCell
        content={<Trash />}
        onClick={() => handleDeleteButtonClick(term)}
      />,
    ];
  };

  const getAddNewTermContent = (category) => {
    const handleClick = () => {
      dispatch(
        addTerm({
          categoryId: category.id,
          indexToPlace: category.terms.length,
          term: createEmptyTerm(category.colour),
        })
      );
    };
    return [
      <AddChildrenCell addChildrenText="Add new Term" onClick={handleClick} />,
    ];
  };

  const getRows = (categories) => {
    const result = [];
    let id = 0;
    categories?.forEach((category, categoryIndex) => {
      result.push({
        content: getCategoryContent(category),
        parentIndex: categoryIndex,
        collapsed: !category.isExpanded,
        newlyCreated: category.newlyCreated || false,
        id,
      });
      id += 1;
      if (category.terms) {
        category.terms.forEach((term) => {
          result.push({
            content: getTermContent(term),
            childrenOf: categoryIndex,
            newlyCreated: term.newlyCreated || false,
            id,
          });
          id += 1;
        });
      }
      if (isEditMode) {
        result.push({
          id,
          childrenOf: categoryIndex,
          content: getAddNewTermContent(category),
        });
        id += 1;
      }
    });
    return result;
  };

  const getTermsState = (tableRows, parentIndex, colour) =>
    tableRows
      .filter(
        (row) =>
          row.childrenOf === parentIndex &&
          !row?.content[0]?.props?.addChildrenText
      )
      .map((row, index) => ({
        name: row.content[0].props.content.props.term.name,
        caseSensitive: row.content[1].props.checked,
        /* If category colour was changed we set it to all category terms,
           but if category colour wasn't change (colour arg is undefined) we set to terms their own colours
        */
        colour:
          colour !== undefined
            ? colour
            : row.content[2].props.content.props.colour,
        description: row.content[3].props.text,
        id: index,
        categoryId: parentIndex,
        dictionaryTermType:
          row.content[4].props.content.props.term.dictionaryTermType ||
          dictionaryTermTypes.STRING,
        error: row.content[0].props.error || "",
        type: "TERM",
      }));

  const getTermColour = (colour, dictionary, parentIndex) => {
    /* Check if category colour was changed. If it was return new colour
       otherwise return undefined.
    */
    if (dictionary.categories[parentIndex].colour !== colour) {
      return colour;
    }
    return undefined;
  };

  const getCategoryState = (dictionary, tableRows, category) => ({
    name: category.content[0].props.content.props.category.name,
    colour: category.content[2].props.content.props.colour,
    description: category.content[3].props.text,
    terms: getTermsState(
      tableRows,
      category.parentIndex,
      getTermColour(
        category.content[2].props.content.props.colour,
        dictionary,
        category.parentIndex
      )
    ),
    id: category.parentIndex,
    type: "CATEGORY",
    isExpanded: !category.collapsed,
    error: category.content[0].props.content.props.category.error || "",
  });

  const getState = (dictionary, tableRows) => {
    const result = [];
    tableRows.forEach((row) => {
      if (row.parentIndex !== undefined) {
        result.push(getCategoryState(dictionary, tableRows, row));
      }
    });
    return result;
  };

  return { getHeader, getRows, getState };
};
